<template>
  <div class="series-show" v-if="showSeries" :style="seriesShowStyles">
    <div class="hero">
      <SeriesShowHero :series="series" :gradient="seriesShowGradient" />
    </div>
    <div class="main">
      <div class="section params">
        <div class="left">
          <h2 class="title">参数</h2>
          <SeriesShowParams :series="series" />
        </div>
        <transition name="series-progress">
          <div
            v-if="showSeriesProgress"
            :class="seriesProgressClasses"
            ref="seriesProgress"
          >
            <SeriesProgress :series="series" :gradient="seriesShowGradient" />
          </div>
        </transition>
      </div>
      <div class="section">
        <h2 class="title">技术</h2>
        <SeriesShowStack :series="series" />
      </div>
      <div class="section" v-if="series.trails">
        <h2 class="title">路线</h2>
        <SeriesShowTrails :series="series" />
      </div>
      <div class="section">
        <h2 class="title">内容</h2>
        <SeriesPackList :series="series" :loading="!showSeriesPackList" />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import SeriesPackList from '../components/series-pack-list.vue';
import SeriesShowParams from '@/series/show/components/series-show-params';
import SeriesShowHero from '@/series/show/components/series-show-hero';
import SeriesShowStack from '@/series/show/components/series-show-stack';
import SeriesProgress from '@/series/components/series-progress';
import { generateGradientStyles } from '@/app/app.service';
import { debounce } from 'debounce';
import SeriesShowTrails from '@/series/show/components/series-show-trails';

export default defineComponent({
  name: 'SeriesShow',

  /**
   * 标题
   */
  title() {
    if (this.showSeries) {
      return this.series.title;
    } else {
      return '加载中...';
    }
  },

  /**
   * 属性
   */
  props: {
    seriesId: String,
  },

  /**
   * 数据
   */
  data() {
    return {
      showSeriesPackList: false,
      fixedSeriesProgress: false,
      showSeriesProgress: true,
    };
  },

  /**
   * 计算属性
   */
  computed: {
    ...mapGetters({
      loading: 'series/show/loading',
      series: 'series/show/series',
    }),

    showSeries() {
      return !this.loading && this.series;
    },

    seriesShowGradient() {
      const gradientStyles = generateGradientStyles(this.series.gradient);

      return {
        background: gradientStyles,
      };
    },

    seriesShowStyles() {
      return {
        '--series-color': this.series.color,
      };
    },

    seriesProgressClasses() {
      return ['right', { fixed: this.fixedSeriesProgress }];
    },
  },

  /**
   * 监视
   */
  watch: {
    loading(newValue) {
      if (newValue) {
        setTimeout(() => {
          this.showSeriesPackList = true;
        }, 200);
      }
    },
  },

  /**
   * 已创建
   */
  async created() {
    await this.getSeriesById(this.seriesId);

    if (window) {
      window.addEventListener('scroll', this.onScrollWindow);
    }
  },

  /**
   * 取消挂载
   */
  unmounted() {
    if (window) {
      window.removeEventListener('scroll', this.onScrollWindow);
    }
  },

  /**
   * 组件方法
   */
  methods: {
    ...mapActions({
      getSeriesById: 'series/show/getSeriesById',
    }),

    onScrollWindow() {
      this.debounceLoadMore();
    },

    debounceLoadMore: debounce(function() {
      if (document) {
        const {
          height,
          top,
        } = this.$refs.seriesProgress.getBoundingClientRect();

        if (top < 0 && top < -height) {
          if (!this.fixedSeriesProgress) {
            this.fixedSeriesProgress = true;
            this.showSeriesProgress = false;

            setTimeout(() => {
              this.showSeriesProgress = true;
            }, 200);
          }
        }

        const { scrollTop } = document.documentElement;

        if (scrollTop === 0) {
          if (this.fixedSeriesProgress) {
            this.fixedSeriesProgress = false;

            this.showSeriesProgress = false;

            setTimeout(() => {
              this.showSeriesProgress = true;
            }, 200);
          }
        }
      }
    }, 200),
  },

  /**
   * 使用组件
   */
  components: {
    SeriesShowTrails,
    SeriesProgress,
    SeriesShowStack,
    SeriesShowHero,
    SeriesShowParams,
    SeriesPackList,
  },
});
</script>

<style scoped>
@import './styles/series-show.css';
</style>
